import { arrayMove, uuid } from "MyDashboards/utils/utils";
import { enablePatches, produce } from "immer";
import { WritableDraft } from "immer/dist/internal";
import create from "zustand";
import { getDashboard, getSlideTemplate } from "../api/dashboards.apis";
import scaleDashboardPages from "../utils/scaleDashboard";
import {
  ActiveEditAction,
  ColorRGB,
  DragInfo,
  EditorMode,
  Element,
  Slide,
  SlideMeta,
  SlideMetaStorage,
} from "./editorTypes";

enablePatches();

const allStates: any[] = [];
const redoBuffer: any[] = [];
let undoIndex = -1;
let lastActiveIndex = -1;
let lastWasUndo = false;
let unsavedChanges = false;

const compare = (a: any, b: any) => {
  if (typeof a !== typeof b) {
    return b;
  }
  if (Array.isArray(a) !== Array.isArray(b)) {
    return b;
  }
  if (typeof a === "object" && typeof b === "object") {
    let diff: any = {};
    Object.keys(a).forEach((key) => {
      if (a[key] !== b[key]) {
        diff[key] = compare(a[key], b[key]);
      }
    });
    return diff;
  }

  if (a !== b) {
    return b;
  }
  return undefined;
};

const useDashboardEditor = create<SlideMetaStorage>((set) => ({
  /**
   * Possibly we need only one Powerpoint report template, switching to this template now
   */
  templateName: "/report/templates/report_slide_templates_en.pptx",
  /**
   * Editor properties
   */
  editorWidth: 900,
  editorHeight: 540,
  scaleFactor: 1,
  isDragged: false,

  selectedElements: [],
  dragInfo: undefined,
  mode: EditorMode.edit,

  /**
   * Page properties
   */
  pages: [],

  hasUnsavedChanges: () => unsavedChanges,
  setUnsavedChanges: (value: boolean) => {
    unsavedChanges = value;
  },

  // updateElementIdsForComponents: (pageId: string, oldElementId:string, newElementId:string) => void;
  reorderPage: (id: string, newPos: number) => {
    set(
      produce<SlideMetaStorage>((state) => {
        const pageIdx = state.pages.findIndex((p) => p.id === id);
        if (pageIdx === -1)
          throw Error("Tried to reorder page with non existing id");
        if (newPos < 0 || newPos >= state.pages.length)
          throw Error(
            "New order position is outside of pages array boundaries"
          );
        state.pages = arrayMove(state.pages, pageIdx, newPos);
      })
    );
  },
  setEditorMode: (mode: EditorMode) => {
    set(
      produce<SlideMetaStorage>((state) => {
        state.mode = mode;
      })
    );
  },
  removeActivePage: () => {
    set(
      produce<SlideMetaStorage>((state) => {
        const pageIndex = state.pages.findIndex(
          (el) => el.id === state.selectedPageId
        );
        if (pageIndex >= 0) {
          state.pages.splice(pageIndex, 1);
          if (state.pages[pageIndex]) {
            state.selectedPageId = state.pages[pageIndex].id;
            return;
          }
          if (pageIndex > 0) {
            state.selectedPageId = state.pages[pageIndex - 1].id;
          } else {
            state.selectedPageId = undefined;
          }
        }
      })
    );
  },
  clearEditor: () => {
    set(
      produce<SlideMetaStorage>((state) => {
        state.pages = [];
      })
    );
  },
  loadDashboard: async (id: string, scaleToWidth: number) => {
    // 1. clear the old pages list
    set(
      produce<SlideMetaStorage>((state) => {
        state.isLoading = true;
        state.pages = [];
      })
    );
    try {
      const old = window.localStorage.getItem(id);
      const board = await getDashboard(id);
      let scaleFactor = 1;
      if (board.dashboardData?.pages?.[0].width) {
        scaleFactor = scaleToWidth / board.dashboardData?.pages?.[0].width;
      }
      const scaledData = {
        pages: scaleDashboardPages(
          scaleFactor,
          board.dashboardData?.pages || []
        ),
      };
      set(
        produce<SlideMetaStorage>((state) => {
          state.pages = scaledData.pages;
          state.scaleFactor = scaleFactor;
          state.isLoading = false;

          if (state.pages.length > 0) {
            state.selectedPageId = state.pages[0].id;
          }
          if (board.dashboardData?.pptFile) {
            state.templateName = board.dashboardData?.pptFile;
          }
        })
      );
      allStates.length = 0;
    } catch (e) {
      console.error(e);
      set(
        produce<SlideMetaStorage>((state) => {
          state.isLoading = false;
        })
      );
    }
  },
  loadSlideTemplate: async (id: string, scaleToWidth: number) => {
    console.log("loadSlideTemplate", id);
    // 1. clear the old pages list
    set(
      produce<SlideMetaStorage>((state) => {
        state.isLoading = true;
        state.pages = [];
      })
    );
    try {
      const old = window.localStorage.getItem(id);
      const board = await getSlideTemplate(id);

      // TODO:
      // - server returns the slide template in wrong format

      console.log("loadSlideTemplate, board", board);
      let scaleFactor = 1;
      if (board.dashboardData?.pages?.[0].width) {
        scaleFactor = scaleToWidth / board.dashboardData?.pages?.[0].width;
      }
      const scaledData = {
        pages: scaleDashboardPages(
          scaleFactor,
          board.dashboardData?.pages || []
        ),
      };
      set(
        produce<SlideMetaStorage>((state) => {
          // state.templateName = board[0].
          /*
          state.editorWidth = board.dashboardData?.pages?.[0].width || state.editorWidth;
          state.editorHeight = board.dashboardData?.pages?.[0].height || state.editorHeight;
          */
          state.pages = scaledData.pages;
          state.scaleFactor = scaleFactor;
          state.isLoading = false;
          if (state.pages.length > 0) {
            state.selectedPageId = state.pages[0].id;
          }
          if (board.dashboardData?.pptFile) {
            state.templateName = board.dashboardData?.pptFile;
          }
        })
      );
    } catch (e) {
      console.error(e);
      set(
        produce<SlideMetaStorage>((state) => {
          state.isLoading = false;
        })
      );
    }
  },
  updateElementIdsForComponents: (
    pageId: string,
    oldElementId: string,
    newElementId: string
  ) =>
    set(
      produce<SlideMetaStorage>((state) => {
        state.pages
          .find((p) => p.id === pageId)
          ?.elements.filter((el) => {
            return (
              el.id === state.selectedElementId ||
              state.selectedElements.includes(el.id)
            );
          })
          .forEach((el) => {
            if (
              el.component &&
              el.component.data &&
              el.component.data.elementId === oldElementId
            ) {
              el.component.data.elementId = newElementId;
            }
          });
      })
    ),
  setEditAction: (data?: ActiveEditAction) =>
    set(
      produce<SlideMetaStorage>((state) => {
        state.editedElement = data;
      })
    ),
  setDragInfo: (dragInfo: Partial<DragInfo>) =>
    set(
      produce<SlideMetaStorage>((state) => {
        if (!state.dragInfo) {
          state.dragInfo = {};
        }
        state.dragInfo = { ...state.dragInfo, ...dragInfo };
      })
    ),
  setDragged: (isDragged: boolean) =>
    set(
      produce<SlideMetaStorage>((state) => {
        state.isDragged = isDragged;
        if (!isDragged) {
          state.dragInfo = {};
        }
      })
    ),

  alignBorders: (
    borderToAlign:
      | "LEFT"
      | "TOP"
      | "BOTTOM"
      | "RIGHT"
      | "WIDTH"
      | "HEIGHT"
      | "HCENTER"
      | "VCENTER"
  ) =>
    set(
      produce<SlideMetaStorage>((state) => {
        const selectedPage = state.pages.find(
          (p) => p.id === state.selectedPageId
        );
        if (!selectedPage) return;

        const pickValue = (el: Element) => {
          if (borderToAlign === "WIDTH") return el.width;
          if (borderToAlign === "HEIGHT") return el.height;
          if (borderToAlign === "LEFT") return el.x;
          if (borderToAlign === "RIGHT") return el.x + el.width;
          if (borderToAlign === "BOTTOM") return el.y + el.height;
          if (borderToAlign === "TOP") return el.y;
          if (borderToAlign === "HCENTER") return el.x + el.width / 2;
          if (borderToAlign === "VCENTER") return el.y + el.height / 2;
          return 0;
        };
        const setValue = (el: Element, deltaValue: number) => {
          if (borderToAlign === "WIDTH") el.width += deltaValue;
          if (borderToAlign === "HEIGHT") el.height += deltaValue;
          if (borderToAlign === "LEFT") el.x += deltaValue;
          if (borderToAlign === "RIGHT") el.x += deltaValue;
          if (borderToAlign === "BOTTOM") el.y += deltaValue;
          if (borderToAlign === "TOP") el.y += deltaValue;
          if (borderToAlign === "HCENTER") el.x += deltaValue;
          if (borderToAlign === "VCENTER") el.y += deltaValue;
        };

        // find common left border
        if (state.selectedElements) {
          const commonValues: Record<number, number> = {};
          const add = (value: number) => {
            const v = Math.round(value);
            if (!commonValues[v]) commonValues[v] = 0;
            commonValues[v] += 1;
          };
          const elements = state.selectedElements
            .map((id) => selectedPage.elements.find((el) => el.id === id))
            .filter((v) => v) as Array<WritableDraft<Element>>;

          elements.forEach((el) => {
            add(pickValue(el));
          });

          const valuesInCommonnessOrder = Object.entries(commonValues).sort(
            (a, b) => {
              return a[1] - b[1];
            }
          );
          let targetValue = 0;
          /**
           * if all values are the same or only one is different, only 1 or 2 values
           */
          if (valuesInCommonnessOrder.length < 3) {
            targetValue = Number(valuesInCommonnessOrder[0][0]); // eslint-disable-line
          }
          if (valuesInCommonnessOrder.length >= 2) {
            if (
              borderToAlign === "WIDTH" ||
              borderToAlign === "HEIGHT" ||
              borderToAlign === "HCENTER" ||
              borderToAlign === "VCENTER"
            ) {
              targetValue =
                valuesInCommonnessOrder
                  .map((v) => Number(v[0]))
                  .reduce((prev, next) => prev + next, 0) /
                valuesInCommonnessOrder.length;
            } else {
              targetValue =
                valuesInCommonnessOrder
                  .map((v) => Number(v[0]))
                  .sort((a, b) => {
                    if (borderToAlign === "LEFT" || borderToAlign === "TOP") {
                      return a - b;
                    }
                    return b - a;
                  })[0] || 0;
            }

            //                 .reduce((prev, next) => prev + next, 0) / valuesInCommonnessOrder.length;
          }
          elements.forEach((el) => {
            console.log("ALIGN, target  ", targetValue, pickValue(el));
            setValue(el, targetValue - pickValue(el));
          });
        }
      })
    ),

  initStore: (meta: SlideMeta, scale?: number) => {
    console.log("initStore was called");

    const scaleFactor = scale || 0.5;

    const scaledData = {
      ...meta,
      pages: meta.pages.map((p) => {
        return {
          ...p,
          width: p.width * scaleFactor,
          height: p.height * scaleFactor,
          elements: p.elements.map((el) => {
            return {
              ...el,
              x: el.x * scaleFactor,
              y: el.y * scaleFactor,
              width: el.width * scaleFactor,
              height: el.height * scaleFactor,
            };
          }),
        };
      }),
    };
    set(
      produce<SlideMetaStorage>((state) => {
        state.pages = scaledData.pages;
        state.scaleFactor = scaleFactor;
      })
    );
  },

  setElementProperties: (
    pageId: string | undefined,
    elementId: string | undefined,
    values: Partial<Element>
  ) => {
    if (!pageId) return;
    if (!elementId) return;
    set(
      produce<SlideMetaStorage>((state) => {
        const pageIndex = state.pages.findIndex((el) => el.id === pageId);
        const elementIndex = state.pages[pageIndex].elements.findIndex(
          (el) => el.id === elementId
        );
        const el = state.pages[pageIndex].elements[elementIndex];
        state.pages[pageIndex].elements[elementIndex] = { ...el, ...values };
      })
    );
  },

  removeSelectedElement: () => {
    set(
      produce<SlideMetaStorage>((state) => {
        const pageIndex = state.pages.findIndex(
          (el) => el.id === state.selectedPageId
        );
        const elementIndex = state.pages[pageIndex].elements.findIndex(
          (el) => el.id === state.selectedElementId
        );

        if (elementIndex >= 0) {
          if (
            state.pages[pageIndex].elements[elementIndex].id ===
            state.selectedElementId
          ) {
            state.selectedElementId = undefined;
          }
          state.pages[pageIndex].elements.splice(elementIndex, 1);
        }

        if (state.selectedElements.length > 0) {
          const matchedElementsIdx: number[] = [];
          state.pages[pageIndex].elements.forEach((el, idx) => {
            if (state.selectedElements.includes(el.id))
              matchedElementsIdx.push(idx);
          });

          matchedElementsIdx.forEach((elementIdx, loopIdx) => {
            state.pages[pageIndex].elements.splice(elementIdx - loopIdx, 1);
          });
        }
      })
    );
  },

  addPage: (newPage: Slide) => {
    set(
      produce<SlideMetaStorage>((state) => {
        const p: Slide = {
          ...newPage,
          id: uuid(),
          elements: newPage.elements.map((e) => ({ ...e, id: uuid() })),
        };
        state.scaleFactor = state.editorWidth / newPage.width;
        const [scaled] = scaleDashboardPages(state.scaleFactor, [p]);
        state.pages.push(scaled);
        state.selectedPageId = p.id;
      })
    );
  },

  addElement: (element: Element) => {
    set(
      produce<SlideMetaStorage>((state) => {
        const page = state.pages.find((p) => p.id === state.selectedPageId);
        if (page) {
          page.elements.push({
            ...element,
            x: element.x + 5,
            y: element.y + 5,
            id: uuid(),
          });
          state.selectedElementId = page.elements[page.elements.length - 1].id;
        }
      })
    );
  },

  cloneActiveElement: () => {
    set(
      produce<SlideMetaStorage>((state) => {
        if (state.selectedElementId && state.selectedPageId) {
          const pageIndex = state.pages.findIndex(
            (el) => el.id === state.selectedPageId
          );
          const elementIndex = state.pages[pageIndex].elements.findIndex(
            (el) => el.id === state.selectedElementId
          );

          const page = state.pages[pageIndex];
          if (page) {
            const element = page.elements[elementIndex];

            if (element) {
              const newID = uuid();
              page.elements.push({
                ...element,
                id: newID,
                x: element.x + 5,
                y: element.y + 5,
              });
              state.selectedElementId = newID;
            }
          }
        }
      })
    );
  },
  setPageProperties: (pageId: string, values: Partial<Slide>) => {
    set(
      produce<SlideMetaStorage>((state) => {
        const pageIndex = state.pages.findIndex((el) => el.id === pageId);
        if (pageIndex >= 0) {
          const el = state.pages[pageIndex];
          state.pages[pageIndex] = { ...el, ...values };
        }
      })
    );
  },
  setSelectedElementProperties: (values: Partial<Element>) => {
    set(
      produce<SlideMetaStorage>((state) => {
        const pageIndex = state.pages.findIndex(
          (el) => el.id === state.selectedPageId
        );
        const elementIndex = state.pages[pageIndex].elements.findIndex(
          (el) => el.id === state.selectedElementId
        );

        if (pageIndex >= 0 && elementIndex >= 0) {
          const el = state.pages[pageIndex].elements[elementIndex];
          state.pages[pageIndex].elements[elementIndex] = { ...el, ...values };
        }
      })
    );
  },
  setColor: (value: ColorRGB) => {
    set(
      produce<SlideMetaStorage>((state) => {
        const selectedElement = state.pages
          .find((el) => el.id === state.selectedPageId)
          ?.elements.find((el) => el.id === state.selectedElementId);

        if (selectedElement) {
          selectedElement?.paragraphs?.forEach((par) => {
            par.portions?.forEach((por) => {
              por.fillColor = value;
            });
          });
        }
      })
    );
  },
  setFontSize: (value: number) => {
    set(
      produce<SlideMetaStorage>((state) => {
        const selectedElement = state.pages
          .find((el) => el.id === state.selectedPageId)
          ?.elements.find((el) => el.id === state.selectedElementId);

        if (selectedElement) {
          selectedElement?.paragraphs?.forEach((par) => {
            par.portions?.forEach((por) => {
              por.fontSize = value;
            });
          });
        }
      })
    );
  },

  undo: () =>
    set(
      produce<SlideMetaStorage>((s) => {
        lastWasUndo = true;
        if (undoIndex >= 0) {
          const state = allStates[lastActiveIndex];
          redoBuffer.push(state);

          if (redoBuffer.length > 20) {
            redoBuffer.shift();
          }

          while (allStates[undoIndex].pages === state.pages && undoIndex >= 0) {
            undoIndex -= 1;
          }
          if (undoIndex < 0) return;
          // const newVersion = allStates[undoIndex].pages;
          // const original = state.pages;
          // console.log('zustand ', JSON.stringify(compare(original, newVersion), null, 2));
          s.pages = allStates[undoIndex].pages;
          lastActiveIndex = undoIndex;
          allStates.length = undoIndex + 1; // remove last item from the undo buffer
          undoIndex -= 1;
        }
      })
    ),
  redo: () =>
    set(
      produce<SlideMetaStorage>((s) => {
        if (redoBuffer.length > 0) {
          const redoState = redoBuffer.pop();
          s.pages = redoState.pages;
        }
      })
    ),
  duplicateSlide: () =>
    set(
      produce<SlideMetaStorage>((state) => {
        const activePage = state.pages.find(
          (p) => p.id === state.selectedPageId
        );
        if (activePage) {
          const newPage = {
            ...activePage,
            id: uuid(),
            elements: activePage.elements.map((el) => {
              return {
                ...el,
                id: uuid(),
              };
            }),
          };
          state.pages.splice(state.pages.indexOf(activePage) + 1, 0, newPage);
        }
      })
    ),
  setSelectedPage: (pageId: string) =>
    set(
      produce<SlideMetaStorage>((state) => {
        state.selectedPageId = pageId;
      })
    ),
  moveZOrder: (amount: number | "TOP" | "BOTTOM") =>
    set(
      produce<SlideMetaStorage>((state) => {
        const pageIndex = state.pages.findIndex(
          (el) => el.id === state.selectedPageId
        );
        const selectedElIndex = state.pages[pageIndex].elements.findIndex(
          (el) => el.id === state.selectedElementId
        );
        if (selectedElIndex >= 0) {
          let newIdx = selectedElIndex;
          if (typeof amount === "number") {
            const testIdx = selectedElIndex + amount;
            if (
              testIdx >= 0 &&
              testIdx < state.pages[pageIndex].elements.length
            ) {
              newIdx = testIdx;
            }
          } else {
            if (amount === "TOP") {
              newIdx = state.pages[pageIndex].elements.length - 1;
            }
            if (amount === "BOTTOM") {
              newIdx = 0;
            }
          }

          if (newIdx !== selectedElIndex) {
            const currElement =
              state.pages[pageIndex].elements[selectedElIndex];
            // remove element from the position
            state.pages[pageIndex].elements.splice(selectedElIndex, 1);
            // insert into another position
            state.pages[pageIndex].elements.splice(newIdx, 0, currElement);
          }
        }
      })
    ),

  setSelectedElement: (elementId?: string, add?: boolean) =>
    set(
      produce<SlideMetaStorage>((state) => {
        if (!elementId) {
          state.selectedElements = [];
          state.selectedElementId = undefined;
          return;
        }
        if (
          add &&
          (state.selectedElements.length > 0 ||
            (state.selectedElementId && elementId !== state.selectedElementId))
        ) {
          if (state.selectedElements.indexOf(elementId) < 0) {
            state.selectedElements?.push(elementId);
          }
          if (
            state.selectedElementId &&
            state.selectedElements.indexOf(state.selectedElementId) < 0
          ) {
            state.selectedElements?.push(state.selectedElementId);
          }
          state.selectedElementId = undefined;
        } else {
          state.selectedElements = [];
          state.selectedElementId = elementId;
        }
      })
    ),
}));

let lastPagesInState: Slide[] | null = null;
useDashboardEditor.subscribe((state) => {
  if (lastPagesInState !== state.pages) {
    if (lastPagesInState !== null) {
      console.log("beforeunload changes for the pages");
      unsavedChanges = true;
    }
  }

  lastPagesInState = state.pages;

  if (lastWasUndo) {
    lastWasUndo = false;
    return;
  }
  if (!lastWasUndo) {
    allStates.push(state);
    undoIndex = allStates.length - 2;
    lastActiveIndex = allStates.length - 1;
  }
  lastWasUndo = false;
});

export default useDashboardEditor;
